(function() {
  'use strict';

  function initWhenReady() {
    // Punch sound URLs
    const punchSounds = [
      'https://cdn.shopify.com/s/files/1/0727/7376/3106/files/canhit1.wav?v=1766369101',
      'https://cdn.shopify.com/s/files/1/0727/7376/3106/files/canhit2.wav?v=1766369101',
      'https://cdn.shopify.com/s/files/1/0727/7376/3106/files/canhit3.wav?v=1766369101',
      'https://cdn.shopify.com/s/files/1/0727/7376/3106/files/canhit4.wav?v=1766369101'
    ];

    // Web Audio setup for punch sounds with rapid cutover and smooth gain ramps
    let audioCtx = null;
    let punchBuffers = [];
    let gainNode = null;
    let currentSource = null;

    async function initAudio() {
      if (audioCtx) return; // already initialized
      audioCtx = new (window.AudioContext || window.webkitAudioContext)();
      gainNode = audioCtx.createGain();
      gainNode.gain.value = 0.5; // global volume 50%
      gainNode.connect(audioCtx.destination);
      // preload the four sounds
      punchBuffers = await Promise.all(
        punchSounds.map(async (url) => {
          const res = await fetch(url);
          const data = await res.arrayBuffer();
          return await audioCtx.decodeAudioData(data);
        })
      );
    }

    async function playRandomPunchSound() {
      await initAudio();
      if (audioCtx.state === 'suspended') {
        try { await audioCtx.resume(); } catch (e) { /* ignore */ }
      }
      const buffer = punchBuffers[Math.floor(Math.random() * punchBuffers.length)];
      if (!buffer) return;
      const now = audioCtx.currentTime;
      // fade out previous source rapidly to avoid click
      if (currentSource) {
        gainNode.gain.setValueAtTime(gainNode.gain.value, now);
        gainNode.gain.linearRampToValueAtTime(0, now + 0.01);
        currentSource.stop(now + 0.01);
      }
      // create new source
      const src = audioCtx.createBufferSource();
      src.buffer = buffer;
      src.connect(gainNode);
      // fade in gain
      gainNode.gain.setValueAtTime(0, now + 0.01);
      gainNode.gain.linearRampToValueAtTime(0.5, now + 0.01);
      src.start(now + 0.01);
      currentSource = src;
    }

    // Clean up any previous viewer (from SPA/AJAX navigation) before re-init
    const cleanupExistingViewer = () => {
      const existing = window.__threeJsModelViewerInstance;
      if (existing?.dispose) {
        try {
          existing.dispose();
        } catch (e) {
          console.warn('dispose failed', e);
        }
      }
      const existingContainer = document.getElementById('threejs-model-viewer');
      if (existingContainer) {
        existingContainer.querySelectorAll('canvas').forEach((c) => c.remove());
      }
      window.__threeJsModelViewerInstance = null;
      window.__threeJsModelViewerInitialized = false;
    };

    cleanupExistingViewer();
    // Create debug overlay
    const debugOverlay = document.createElement('div');
    debugOverlay.id = 'threejs-debug-overlay';
    debugOverlay.style.cssText = `
      position: fixed;
      top: 10px;
      left: 10px;
      background: rgba(0, 0, 0, 0.9);
      color: #0f0;
      font-family: monospace;
      padding: 15px;
      border-radius: 5px;
      font-size: 12px;
      z-index: 99999;
      max-width: 400px;
      border: 2px solid #0f0;
      display: none;
    `;
    document.body.appendChild(debugOverlay);

    // Add styles for pointer lock cursor hiding
    const style = document.createElement('style');
    style.textContent = `
      body.threejs-dragging #animated-cursor {
        display: none !important;
      }
      body.threejs-dragging {
        cursor: none !important;
      }
    `;
    document.head.appendChild(style);

    const addDebug = (msg) => {
      console.log(msg);
      debugOverlay.innerHTML += msg + '<br>';
    };

    const setInitState = (success) => {
      window.__threeJsModelViewerInitializing = false;
      window.__threeJsModelViewerInitialized = !!success;
    };

    addDebug('🚀 Three.js Model Viewer Initializing');

    if (window.__threeJsModelViewerInitialized || window.__threeJsModelViewerInitializing) {
      addDebug('⚠️ Already initialized, skipping');
      return;
    }
    window.__threeJsModelViewerInitializing = true;

    // Load Three.js
    if (typeof THREE === 'undefined') {
      addDebug('📦 Loading THREE.js from CDN');
      const script = document.createElement('script');
      script.src = 'https://cdn.jsdelivr.net/npm/three@0.128.0/build/three.min.js';
      script.onload = () => {
        addDebug('✅ THREE.js loaded');
        loadDependencies();
      };
      script.onerror = () => {
        addDebug('❌ Failed to load THREE.js');
        setInitState(false);
      };
      document.head.appendChild(script);
    } else {
      addDebug('✅ THREE.js already loaded');
      loadDependencies();
    }

    function loadDependencies() {
      // Load GLTFLoader - CORRECTED PATH
      if (typeof THREE.GLTFLoader === 'undefined') {
        addDebug('📦 Loading GLTFLoader');
        const gltfScript = document.createElement('script');
        gltfScript.src = 'https://cdn.jsdelivr.net/npm/three@0.128.0/examples/js/loaders/GLTFLoader.js';
        gltfScript.onload = () => {
          addDebug('✅ GLTFLoader loaded');
          addDebug('GLTFLoader type: ' + typeof THREE.GLTFLoader);
          loadOrbitControls();
        };
        gltfScript.onerror = () => {
          addDebug('❌ Failed to load GLTFLoader');
          setInitState(false);
        };
        document.head.appendChild(gltfScript);
      } else {
        addDebug('✅ GLTFLoader already loaded');
        loadOrbitControls();
      }
    }

    function loadOrbitControls() {
      // Load TrackballControls instead - allows true 360 rotation
      if (typeof THREE.TrackballControls === 'undefined') {
        addDebug('📦 Loading TrackballControls');
        const controlsScript = document.createElement('script');
        controlsScript.src = 'https://cdn.jsdelivr.net/npm/three@0.128.0/examples/js/controls/TrackballControls.js';
        controlsScript.onload = () => {
          addDebug('✅ TrackballControls loaded');
          addDebug('TrackballControls type: ' + typeof THREE.TrackballControls);
          loadRGBELoader();
        };
        controlsScript.onerror = () => {
          addDebug('❌ Failed to load TrackballControls');
          setInitState(false);
        };
        document.head.appendChild(controlsScript);
      } else {
        addDebug('✅ TrackballControls already loaded');
        loadRGBELoader();
      }
    }

    function loadRGBELoader() {
      // Load RGBELoader for HDRI support
      if (typeof THREE.RGBELoader === 'undefined') {
        addDebug('📦 Loading RGBELoader');
        const rgbeScript = document.createElement('script');
        rgbeScript.src = 'https://cdn.jsdelivr.net/npm/three@0.128.0/examples/js/loaders/RGBELoader.js';
        rgbeScript.onload = () => {
          addDebug('✅ RGBELoader loaded');
          initViewer();
        };
        rgbeScript.onerror = () => {
          addDebug('❌ Failed to load RGBELoader');
          initViewer(); // Continue without HDRI
        };
        document.head.appendChild(rgbeScript);
      } else {
        addDebug('✅ RGBELoader already loaded');
        initViewer();
      }
    }

    function initViewer() {
      addDebug('🎬 Starting viewer initialization');

      const container = document.getElementById('threejs-model-viewer');
      if (!container) {
        addDebug('❌ Container #threejs-model-viewer not found!');
        setInitState(false);
        return;
      }

      addDebug('✅ Container found');
      addDebug('Size: ' + container.clientWidth + 'x' + container.clientHeight);

      // Scene setup
      const scene = new THREE.Scene();
      scene.background = null; // Transparent background
      window.__threeJsScene = scene; // Store for HDRI loading
      addDebug('✅ Scene created');

      // Camera
      const width = container.clientWidth;
      const height = container.clientHeight;
      if (!width || !height) {
        addDebug('❌ Container has zero size, retrying later');
        setInitState(false);
        return;
      }
      const camera = new THREE.PerspectiveCamera(45, width / height, 0.1, 1000);
      camera.position.set(0, 0, 7.5);
      camera.lookAt(0, 0, 0);
      addDebug('✅ Camera created');

      // Renderer FIRST
      const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
      renderer.setSize(width, height);
      renderer.setPixelRatio(window.devicePixelRatio);
      
      // For r128, use this property name:
      renderer.outputEncoding = THREE.sRGBEncoding;
      renderer.toneMapping = THREE.ACESFilmicToneMapping;
      renderer.toneMappingExposure = 0.3;
      
      container.appendChild(renderer.domElement);
      addDebug('✅ Renderer added to DOM');

      // Load HDRI environment
      let envMap = null;
      const hdriUrl = 'https://cdn.shopify.com/s/files/1/0727/7376/3106/files/studio_small_09_1k.hdr?v=1766357808';
      
      if (typeof THREE.RGBELoader !== 'undefined') {
        addDebug('📥 Loading HDRI');
        const rgbeLoader = new THREE.RGBELoader();
        rgbeLoader.load(hdriUrl, (texture) => {
          addDebug('✅ HDRI loaded');
          const pmremGenerator = new THREE.PMREMGenerator(renderer);
          envMap = pmremGenerator.fromEquirectangular(texture).texture;
          texture.dispose();
          
          const scene = window.__threeJsScene;
          if (scene) {
            scene.environment = envMap;
            scene.environmentIntensity = 1.2;
            addDebug('✅ HDRI applied to scene');
          }
        }, undefined, (error) => {
          addDebug('⚠️ HDRI failed to load, using fallback lighting');
          console.error('HDRI load error:', error);
        });
      }

      // Lighting
      const ambientLight = new THREE.AmbientLight(0xffffff, 1.0);
      scene.add(ambientLight);

      const directionalLight = new THREE.DirectionalLight(0xffffff, 1.2);
      directionalLight.position.set(5, 5, 5);
      scene.add(directionalLight);
      addDebug('✅ Lights added');

      // Manual rotation controls for unlimited rotation
      addDebug('✅ Manual rotation controls initialized');
      let isDragging = false;
      let previousMouseX = 0;
      let previousMouseY = 0;
      let rotationX = 0;
      let rotationY = 0;
      let velocityX = 0;
      let velocityY = 0;
      let canModel = null; // Will store the loaded model
      let initialQuaternion = new THREE.Quaternion(); // Store initial rotation
      let returnAnimationId = null; // Track return animation
      let momentumAnimationId = null; // Track momentum animation
      let raycaster = new THREE.Raycaster(); // For click detection
      let mouse = new THREE.Vector2(); // Mouse position for raycasting
      let mouseDownTime = 0; // Track click duration
      let pointerLockTimeout = null; // Delay before activating pointer lock
      let hasMoved = false; // Track if mouse has moved since mousedown
      let autoRotateAnimationId = null; // Track auto-rotate animation

      // Load model
      const loader = new THREE.GLTFLoader();
      const modelUrl = document.getElementById('threejs-model-viewer').dataset.modelUrl;
      
      if (!modelUrl) {
        addDebug('❌ No model URL found in data-model-url');
        return;
      }

      addDebug('📥 Loading model: ' + modelUrl.substring(0, 60) + '...');
      
      loader.load(
      modelUrl,
      (gltf) => {
        addDebug('✅ Model loaded successfully!');
        const loadedModel = gltf.scene;
        
        // Create a wrapper group that will be at world origin
        const modelWrapper = new THREE.Group();
        
        // Enhance materials with environment map
        const envMap = scene.environment;
        
        loadedModel.traverse((child) => {
          if (child.isMesh && child.material) {
            if (Array.isArray(child.material)) {
              child.material.forEach(mat => {
                if (envMap) {
                  mat.envMap = envMap;
                  mat.envMapIntensity = 1.5;
                }
                mat.toneMapped = true;
              });
            } else {
              if (envMap) {
                child.material.envMap = envMap;
                child.material.envMapIntensity = 1.5;
              }
              child.material.toneMapped = true;
            }
            
            // Center each mesh's geometry at its local origin
            if (child.geometry) {
              child.geometry.center();
            }
          }
        });
        
        // Calculate bounding box of the entire loaded model
        const box = new THREE.Box3().setFromObject(loadedModel);
        const center = box.getCenter(new THREE.Vector3());
        
        // Offset the loaded model so its center is at the wrapper's origin
        loadedModel.position.sub(center);
        
        // Add the loaded model to the wrapper
        modelWrapper.add(loadedModel);
        
        // Add wrapper to scene at world origin
        scene.add(modelWrapper);
        
        // Store the wrapper as the model to rotate
        canModel = modelWrapper;
        
        // Store initial quaternion (identity - no rotation)
        initialQuaternion.copy(modelWrapper.quaternion);
        
        // Start auto-rotate on load
        startAutoRotate();
        
        addDebug('Model center: ' + center.x.toFixed(2) + ', ' + center.y.toFixed(2) + ', ' + center.z.toFixed(2));
        addDebug('Wrapper position: ' + modelWrapper.position.x.toFixed(2) + ', ' + modelWrapper.position.y.toFixed(2) + ', ' + modelWrapper.position.z.toFixed(2));
        addDebug('✅ Model positioned at origin');
        addDebug('🎉 Viewer ready!');
      },
      (progress) => {
        if (progress.total > 0) {
          const percent = (progress.loaded / progress.total * 100).toFixed(0);
          addDebug('Loading: ' + percent + '%');
        }
      },
      (error) => {
        addDebug('❌ Error loading model:');
        addDebug('Error type: ' + error.constructor.name);
        addDebug('Message: ' + (error.message || 'Unknown error'));
        console.error('Full error:', error);
        setInitState(false);
      }
    );

    // Pointer/Touch event handlers with Pointer Lock for unlimited rotation
    function onPointerDown(event) {
      mouseDownTime = Date.now(); // Record click time
      hasMoved = false; // Reset move flag
      
      // For mouse, delay pointer lock activation by 200ms to distinguish clicks from drags
      if (!event.touches) {
        // Clear any previous timeout
        if (pointerLockTimeout) {
          clearTimeout(pointerLockTimeout);
        }
        
        // Activate pointer lock after 50ms
        pointerLockTimeout = setTimeout(() => {
          // Only set isDragging and cancel animations when we're ACTUALLY starting a drag
          isDragging = true;
          
          if (momentumAnimationId) {
            cancelAnimationFrame(momentumAnimationId);
            momentumAnimationId = null;
          }
          if (returnAnimationId) {
            cancelAnimationFrame(returnAnimationId);
            returnAnimationId = null;
          }
          
          stopAutoRotate(); // Stop auto-rotate when dragging
          
          renderer.domElement.requestPointerLock();
          document.body.classList.add('threejs-dragging');
          pointerLockTimeout = null;
          
          // Only NOW reset velocity for actual drags
          velocityX = 0;
          velocityY = 0;
        }, 200);
      } else {
        // For touch, set isDragging immediately (no delay like mouse)
        isDragging = true;
        previousMouseX = event.touches[0].clientX;
        previousMouseY = event.touches[0].clientY;
        hasMoved = false;
      }
    }

    function onPointerMove(event) {
      // Only process if pointer lock is active (for mouse) or it's a touch event
      const isPointerLocked = document.pointerLockElement === renderer.domElement;
      const isTouch = event.touches && event.touches.length > 0;
      
      if (!isDragging || !canModel || (!isPointerLocked && !isTouch)) {
        return;
      }
      
      // If touch is moving and we haven't marked movement yet, cancel animations
      if (isTouch && !hasMoved) {
        hasMoved = true;
        
        if (momentumAnimationId) {
          cancelAnimationFrame(momentumAnimationId);
          momentumAnimationId = null;
        }
        if (returnAnimationId) {
          cancelAnimationFrame(returnAnimationId);
          returnAnimationId = null;
        }
        
        document.body.style.overflow = 'hidden';
        velocityX = 0;
        velocityY = 0;
      }
      
      // If mouse is moving and we haven't marked movement yet, activate drag immediately
      if (!isTouch && !hasMoved) {
        hasMoved = true;
        
        // Activate pointer lock and hide cursor immediately
        renderer.domElement.requestPointerLock();
        document.body.classList.add('threejs-dragging');
        
        // Cancel the delayed pointer lock since we're doing it now
        if (pointerLockTimeout) {
          clearTimeout(pointerLockTimeout);
          pointerLockTimeout = null;
        }
        
        // Set isDragging and cancel animations now
        isDragging = true;
        
        if (momentumAnimationId) {
          cancelAnimationFrame(momentumAnimationId);
          momentumAnimationId = null;
        }
        if (returnAnimationId) {
          cancelAnimationFrame(returnAnimationId);
          returnAnimationId = null;
        }
        
        velocityX = 0;
        velocityY = 0;
      }
      
      let deltaX, deltaY;
      
      // Use movementX/Y if pointer is locked (mouse), otherwise calculate delta (touch)
      if (document.pointerLockElement === renderer.domElement) {
        // Pointer lock active - use raw movement deltas (unlimited!)
        deltaX = event.movementX || 0;
        deltaY = event.movementY || 0;
      } else if (event.touches) {
        // Touch - calculate delta from position
        const currentX = event.touches[0].clientX;
        const currentY = event.touches[0].clientY;
        
        deltaX = currentX - previousMouseX;
        deltaY = currentY - previousMouseY;
        
        previousMouseX = currentX;
        previousMouseY = currentY;
        
        event.preventDefault();
      } else {
        // Fallback for mouse without pointer lock
        const currentX = event.clientX;
        const currentY = event.clientY;
        
        deltaX = currentX - previousMouseX;
        deltaY = currentY - previousMouseY;
        
        previousMouseX = currentX;
        previousMouseY = currentY;
      }
      
      velocityX = deltaX;
      velocityY = deltaY;
      
      // Rotate around screen-space axes
      const cameraRight = new THREE.Vector3();
      const cameraUp = new THREE.Vector3();
      
      camera.getWorldDirection(cameraRight);
      cameraRight.cross(camera.up).normalize();
      cameraUp.copy(camera.up).normalize();
      
      const qY = new THREE.Quaternion();
      const qX = new THREE.Quaternion();
      
      // Use lower sensitivity for touch events (mobile)
      const sensitivity = event.touches ? 0.012 : 0.01;
      
      qY.setFromAxisAngle(cameraUp, deltaX * sensitivity);
      qX.setFromAxisAngle(cameraRight, deltaY * sensitivity);
      
      canModel.quaternion.premultiply(qY);
      canModel.quaternion.premultiply(qX);
    }

    function onPointerUp(event) {
      // Handle both mouse and touch end
      const isTouch = event.touches !== undefined;
      
      if (isTouch) {
        // Touch end
        isDragging = false;
        document.body.style.overflow = '';
        applyMomentum();
      }
      // Mouse end is handled in the mouseup listener below
    }

    // Handle pointer lock change (user presses ESC, etc)
    document.addEventListener('pointerlockchange', () => {
      if (document.pointerLockElement !== renderer.domElement) {
        // Pointer lock exited - remove dragging class
        document.body.classList.remove('threejs-dragging');
        if (isDragging) {
          isDragging = false;
          applyMomentum();
        }
      }
    });

    function applyMomentum() {
      if (!canModel) return;
      
      const friction = .99;
      const minVelocity = 0.01;
      
      function animate() {
        if (Math.abs(velocityX) < minVelocity && Math.abs(velocityY) < minVelocity) {
          // Momentum finished, now return to initial orientation
          momentumAnimationId = null;
          returnToInitialOrientation();
          return;
        }
        
        // Same screen-space rotation for momentum
        const cameraRight = new THREE.Vector3();
        const cameraUp = new THREE.Vector3();
        
        camera.getWorldDirection(cameraRight);
        cameraRight.cross(camera.up).normalize();
        cameraUp.copy(camera.up).normalize();
        
        const qY = new THREE.Quaternion();
        const qX = new THREE.Quaternion();
        
        qY.setFromAxisAngle(cameraUp, velocityX * 0.01);
        qX.setFromAxisAngle(cameraRight, velocityY * 0.01);
        
        canModel.quaternion.premultiply(qY);
        canModel.quaternion.premultiply(qX);
        
        velocityX *= friction;
        velocityY *= friction;
        
        momentumAnimationId = requestAnimationFrame(animate);
      }
      
      momentumAnimationId = requestAnimationFrame(animate);
    }

    function returnToInitialOrientation() {
      if (!canModel) return;
      
      const duration = 1000; // 1 second return animation
      const startTime = Date.now();
      const startQuaternion = canModel.quaternion.clone();
      
      function animate() {
        const elapsed = Date.now() - startTime;
        const progress = Math.min(elapsed / duration, 1); // 0 to 1
        
        // Easing function for smooth deceleration
        const easeProgress = 1 - Math.pow(1 - progress, 3); // Ease out cubic
        
        // Interpolate between current and initial quaternion
        canModel.quaternion.slerpQuaternions(startQuaternion, initialQuaternion, easeProgress);
        
        if (progress < 1) {
          returnAnimationId = requestAnimationFrame(animate);
        } else {
          returnAnimationId = null; // Clear when done
          // Start auto-rotate when return animation finishes
          startAutoRotate();
        }
      }
      
      returnAnimationId = requestAnimationFrame(animate);
    }

    function startAutoRotate() {
      if (!canModel || autoRotateAnimationId) return;
      
      let rotationSpeed = 0.0005; // Slow rotation speed
      
      function animate() {
        if (!canModel) return;
        
        // Rotate around Y axis (vertical) to show the can art
        const qRotate = new THREE.Quaternion();
        qRotate.setFromAxisAngle(new THREE.Vector3(0, 1, 0), rotationSpeed);
        canModel.quaternion.multiplyQuaternions(qRotate, canModel.quaternion);
        
        autoRotateAnimationId = requestAnimationFrame(animate);
      }
      
      autoRotateAnimationId = requestAnimationFrame(animate);
    }

    function stopAutoRotate() {
      if (autoRotateAnimationId) {
        cancelAnimationFrame(autoRotateAnimationId);
        autoRotateAnimationId = null;
      }
    }

    // Mouse events
    renderer.domElement.addEventListener('mousedown', onPointerDown);
    document.addEventListener('mousemove', onPointerMove);
    
    // Use mouseup for punch detection (after pointer lock releases)
    document.addEventListener('mouseup', (event) => {
      // Cancel any pending timeouts
      if (pointerLockTimeout) {
        clearTimeout(pointerLockTimeout);
        pointerLockTimeout = null;
      }
      
      const clickDuration = Date.now() - mouseDownTime;
      const wasQuickClick = clickDuration < 50; // Less than 50ms = click, not drag
      
      if (isDragging) {
        // This was an actual drag, clean up
        isDragging = false;
        
        // Exit pointer lock if active
        if (document.pointerLockElement === renderer.domElement) {
          document.exitPointerLock();
        }
        
        // Remove dragging class to show cursor again
        document.body.classList.remove('threejs-dragging');
        document.body.style.overflow = '';
        
        addDebug('Drag ended (duration: ' + clickDuration + 'ms), applying momentum');
        applyMomentum();
        return;
      }
      
      // Not dragging - this was just a click/punch
      // Make sure cursor is visible and never was hidden
      document.body.classList.remove('threejs-dragging');
      addDebug('Quick click detected (duration: ' + clickDuration + 'ms)');
      
      // Check if clicking on canvas
      if (event.target !== renderer.domElement) {
        addDebug('Click not on canvas');
        return;
      }
      
      // Calculate mouse position in normalized device coordinates
      const rect = renderer.domElement.getBoundingClientRect();
      mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
      mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
      
      addDebug('🖱️ Punch at: ' + mouse.x.toFixed(2) + ', ' + mouse.y.toFixed(2));
      
      // Update the picking ray with the camera and mouse position
      raycaster.setFromCamera(mouse, camera);
      
      // Check if ray hits the model
      const intersects = raycaster.intersectObject(canModel, true);
      addDebug('Intersects: ' + intersects.length);
      
      if (intersects.length > 0) {
        addDebug('✊ HIT THE CAN!');
        playRandomPunchSound();
        
        // Get the hit point
        const hitPoint = intersects[0].point;
        
        // Calculate direction from center to hit point
        const hitDir = hitPoint.clone().normalize();
        
        // Map hit point to screen space impulse
        const impulseMagnitude = 30;
        velocityX = hitDir.x * impulseMagnitude;
        velocityY = -hitDir.y * impulseMagnitude;
        
        addDebug('Impulse: vX=' + velocityX.toFixed(1) + ' vY=' + velocityY.toFixed(1));
        
        // Cancel any ongoing animations
        if (momentumAnimationId) {
          cancelAnimationFrame(momentumAnimationId);
          momentumAnimationId = null;
        }
        if (returnAnimationId) {
          cancelAnimationFrame(returnAnimationId);
          returnAnimationId = null;
        }
        
        // Start momentum with the punch
        applyMomentum();
      }
    });

    // Touch events
    renderer.domElement.addEventListener('touchstart', onPointerDown);
    document.addEventListener('touchmove', onPointerMove, { passive: false });
    document.addEventListener('touchend', onPointerUp);

    // Handle window resize
    window.addEventListener('resize', () => {
      const newWidth = container.clientWidth;
      const newHeight = container.clientHeight;
      camera.aspect = newWidth / newHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(newWidth, newHeight);
    });

    // Ensure size/aspect is corrected after first layout paint
    requestAnimationFrame(() => {
      const w = container.clientWidth || width;
      const h = container.clientHeight || height;
      if (w && h) {
        camera.aspect = w / h;
        camera.updateProjectionMatrix();
        renderer.setSize(w, h);
      }
    });

    // Animation loop
    function animate() {
      requestAnimationFrame(animate);
      renderer.render(scene, camera);
    }

    addDebug('▶️ Animation loop started');
    animate();

    window.__threeJsModelViewerInstance = {
      scene,
      camera,
      renderer,
      dispose() {
        renderer.dispose();
        scene.traverse((object) => {
          if (object.geometry) object.geometry.dispose();
          if (object.material) {
            if (Array.isArray(object.material)) {
              object.material.forEach(mat => mat.dispose());
            } else {
              object.material.dispose();
            }
          }
        });
      }
    };

    setInitState(true);
    }
  }

  // More robust initialization that handles async page loads and Shopify sections
  function tryInit() {
    const container = document.getElementById('threejs-model-viewer');
    if (container && !window.__threeJsModelViewerInitialized) {
      console.log('✅ Container found, initializing...');
      initWhenReady();
      return true;
    }
    return false;
  }

  // Try immediately
  if (tryInit()) {
    // Success
  } else {
    // Container not found, set up observers
    console.log('⏳ Container not found, waiting...');
    
    // Method 1: Poll every 100ms for up to 5 seconds
    let attempts = 0;
    const maxAttempts = 50;
    const pollInterval = setInterval(() => {
      attempts++;
      if (tryInit()) {
        clearInterval(pollInterval);
        console.log('✅ Container found after ' + attempts + ' attempts');
      } else if (attempts >= maxAttempts) {
        clearInterval(pollInterval);
        console.error('❌ Container never appeared after 5 seconds');
      }
    }, 100);
    
    // Method 2: Also use MutationObserver for immediate detection
    const observer = new MutationObserver(() => {
      if (tryInit()) {
        observer.disconnect();
        clearInterval(pollInterval);
        console.log('✅ Container detected by MutationObserver');
      }
    });
    
    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  // Method 3: Listen for Shopify section load events
  document.addEventListener('shopify:section:load', (event) => {
    console.log('🔄 Shopify section loaded:', event.detail?.sectionId);
    // Reset the initialization flag to allow re-initialization
    if (event.detail?.sectionId) {
      window.__threeJsModelViewerInitialized = false;
      tryInit();
    }
  });
})();
